import * as Avatar from '-/Custom/Avatar';
import * as Header from '-/Custom/Header';
import { Stack, useRouter } from 'expo-router';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMutation } from 'urql';
import { v4 } from 'uuid';
import type { ChatCreateGroupFormValues } from '@/components/Chat/ChatCreateGroup.types';
import ChatInfoInputs from '@/components/Chat/ChatInfoInputs';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import Box from '@/components/UI/Box';
import Row from '@/components/UI/Row';
import Text from '@/components/UI/Text';
import { graphql } from '@/gql/graphql';
import useGenerateImageUploadUrls from '@/hooks/useGenerateImageUploadUrls';
import useUploadImage from '@/hooks/useUploadImage';
import useUserId from '@/hooks/useUserId';
import getAvatarText from '@/utils/getAvatarText';
import getOffsetColor from '@/utils/getOffsetColor';
import pickAvatar from '@/utils/pickAvatar';

const InsertGroupChat = graphql(/* GraphQL */ `
  mutation InsertGroupChat(
    $id: uuid!
    $personIds: [chat_person_insert_input!]!
    $title: String!
    $color: String
    $avatarId: uuid
  ) {
    insert_chat_one(
      object: {
        id: $id
        chat_people: { data: $personIds }
        title: $title
        color: $color
        avatar_id: $avatarId
      }
    ) {
      id
    }
  }
`);

export default function ChatCreateGroupInfo() {
  const userId = useUserId();
  const uploadImage = useUploadImage();
  const generateImageUploadUrls = useGenerateImageUploadUrls();
  const { back, dismissAll, navigate } = useRouter();
  const form = useFormContext<ChatCreateGroupFormValues>();

  const title = useWatch({
    control: form.control,
    name: 'title',
    defaultValue: '',
  });
  const color = useWatch({
    control: form.control,
    name: 'color',
    defaultValue: null,
  });
  const localAvatarUrl = useWatch({
    control: form.control,
    name: 'localAvatarUrl',
    defaultValue: null,
  });
  const people = useWatch({
    control: form.control,
    name: 'people',
    defaultValue: [],
  });

  const normalizedTitle = title?.trim() ?? '';
  const isSaveDisabled = normalizedTitle.length < 3;

  const [, insertGroupChat] = useMutation(InsertGroupChat);

  const onPressCamera = async () => {
    const image = await pickAvatar();
    if (image?.uri) {
      form.setValue('localAvatarUrl', image.uri, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <>
      <Stack.Screen options={{ title: title ?? 'New Group Chat' }} />

      <ModalKeyboardAwareScrollView>
        <Header.Toolbar>
          <Header.Row>
            <Avatar.Pressable onPress={onPressCamera}>
              <Avatar.default color={color} size="xl">
                <Avatar.Icon
                  name="camera-outline"
                  size="4xl"
                  color={getOffsetColor(color || '', false)}
                />
                {localAvatarUrl ? (
                  <Avatar.Image localUrl={localAvatarUrl} size="xl" />
                ) : null}
              </Avatar.default>
            </Avatar.Pressable>
            <Header.Title>{title || 'Group Chat'}</Header.Title>
          </Header.Row>
        </Header.Toolbar>
        {color ? <Header.ColorBar backgroundColor={color} /> : null}

        <ChatInfoInputs
          title={title}
          titlePlaceholder="Enter Title..."
          color={color}
          onChangeTitle={(next) =>
            form.setValue('title', next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          onChangeColor={(next) =>
            form.setValue('color', next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />

        <Box space="sm">
          <Box px="lg">
            <Text size="xl" bold>
              {people.length + 1} Participants
            </Text>
            <Text variant="typographySecondary">
              You and {people.length} selected member
              {people.length === 1 ? '' : 's'}
            </Text>
          </Box>

          {[...people].map((person) => (
            <Row key={person.id}>
              <Avatar.default
                color={person.color}
                id={person.avatar_id || ''}
                name={getAvatarText(person.name || '')}
                size="md"
              />
              <Text flexShrink={1} numberOfLines={1} size="xl">
                {person.name}
              </Text>
            </Row>
          ))}
        </Box>
      </ModalKeyboardAwareScrollView>

      <ModalKeyboardStickyView>
        <FormSaveButton
          info={{ text: 'Create', action: 'success' }}
          isDisabled={isSaveDisabled}
          onPress={() =>
            form.handleSubmit(async (values) => {
              const chatId = v4();

              const selectedIds = values.people.flatMap((person) =>
                person.id ? [person.id] : [],
              );
              const uniqueIds = Array.from(
                new Set([...selectedIds, userId]),
              ).filter(Boolean);
              const personIdsInput = uniqueIds.map((id) => ({ person_id: id }));

              let nextAvatarId: string | null = values.avatarId;

              if (values.localAvatarUrl) {
                const { data: uploadUrls } = await generateImageUploadUrls(1);
                const uploadUrl =
                  uploadUrls?.generateImageUploadUrls?.[0]?.uploadURL;

                nextAvatarId = await uploadImage(
                  `chat/${chatId}/avatar`,
                  values.localAvatarUrl,
                  uploadUrl || '',
                );
              }

              const { data: insertData } = await insertGroupChat({
                id: chatId,
                personIds: personIdsInput,
                title: values.title,
                color: values.color,
                avatarId: nextAvatarId,
              });

              if (insertData?.insert_chat_one?.id) {
                back();
                dismissAll();
                navigate({
                  pathname: '/chat/[chatId]',
                  params: { chatId: insertData.insert_chat_one.id },
                });
              }
            })()
          }
        />
      </ModalKeyboardStickyView>
    </>
  );
}
